import { useMemo, useRef, useState } from "react";
import { streamChat } from "../lib/api";
import { parseAgentPlan } from "../lib/agents";
import { extractArtifacts } from "../lib/artifacts";
import { ARTIFACT_PROMPT } from "../lib/defaults";
import { hydrateMessageAttachments } from "../lib/indexedDb";
import { findMode } from "../lib/modes";
import {
  buildAgentPrompt,
  buildCouncilParticipantPrompt,
  buildCouncilSchedule,
  buildCouncilSynthesisPrompt,
  buildFallbackPlan,
  buildTeamSynthesisPrompt,
  buildVerificationPrompt,
  canStartModelCall,
  resolveAgentWaves,
} from "../lib/orchestration";
import { compileSystemPrompt, buildPromptLayers, visibleBranch } from "../lib/prompts";
import {
  buildQuestionAnalysisPrompt,
  buildQuestionDossier,
  capNewQuestions,
  engineAfterQuestions,
  formatQuestions,
  parseQuestionDecision,
  selectNewQuestions,
  shouldEndQuestionLoop,
} from "../lib/questions";
import { useAppStore } from "../lib/store";
import type {
  Artifact,
  Attachment,
  ChatMessage,
  ChatRequest,
  ModelParameters,
  Provider,
  RawTrace,
  RunRecord,
  RunStep,
  SourceCitation,
  StreamEvent,
  Usage,
  QuestionSession,
} from "../lib/types";

export interface CouncilContribution {
  id: string;
  provider: Provider;
  model: string;
  round: number;
  phase: "independent" | "critique";
  content: string;
  status:
    | "queued"
    | "running"
    | "complete"
    | "error"
    | "skipped"
    | "aborted"
    | "cost_limit";
  usage?: Usage;
}

export interface AgentRun {
  id: string;
  agentId: string;
  name: string;
  role: string;
  provider: Provider;
  model: string;
  task: string;
  dependsOn: string[];
  content: string;
  status:
    | "planning"
    | "fallback"
    | "queued"
    | "waiting"
    | "running"
    | "complete"
    | "error"
    | "blocked"
    | "aborted"
    | "cost_limit";
  detail?: string;
  usage?: Usage;
}

interface SendOptions {
  content: string;
  attachments?: Attachment[];
  instruction?: string;
  replaceAfterMessageId?: string;
}

function sumTokens(items: Array<{ usage?: Usage }>): number {
  return items.reduce((total, item) => total + (item.usage?.totalTokens ?? 0), 0);
}

function councilRunRecord(
  contributions: CouncilContribution[],
  startedAt: number,
  knownCost: number,
): RunRecord {
  return {
    engine: "council",
    startedAt,
    finishedAt: Date.now(),
    knownCost,
    totalTokens: sumTokens(contributions),
    steps: contributions.map((item) => ({
      id: item.id,
      label: item.model,
      sublabel: `Round ${item.round} · ${
        item.phase === "independent" ? "indipendente" : "critica"
      }`,
      status: item.status,
      content: item.content,
      round: item.round,
      ...(item.usage ? { usage: item.usage } : {}),
    })) satisfies RunStep[],
  };
}

function teamRunRecord(
  runs: AgentRun[],
  startedAt: number,
  knownCost: number,
): RunRecord {
  return {
    engine: "agent_team",
    startedAt,
    finishedAt: Date.now(),
    knownCost,
    totalTokens: sumTokens(runs),
    steps: runs.map((item) => ({
      id: item.id,
      label: item.name,
      sublabel: item.role,
      status: item.status,
      content: item.content,
      ...(item.detail ? { detail: item.detail } : {}),
      ...(item.usage ? { usage: item.usage } : {}),
    })) satisfies RunStep[],
  };
}

export function useChat() {
  const store = useAppStore();
  const [isStreaming, setIsStreaming] = useState(false);
  const [council, setCouncil] = useState<CouncilContribution[]>([]);
  const [agentRuns, setAgentRuns] = useState<AgentRun[]>([]);
  const [error, setError] = useState<string>();
  const abortRef = useRef<AbortController | null>(null);

  const conversation = useMemo(
    () => store.conversations.find((item) => item.id === store.activeConversationId),
    [store.conversations, store.activeConversationId],
  );
  const questionSession = useMemo(
    () =>
      store.questionSessions.find(
        (session) =>
          session.conversationId === store.activeConversationId &&
          ["analyzing", "collecting", "awaiting_user", "generating_final"].includes(
            session.status,
          ),
      ),
    [store.questionSessions, store.activeConversationId],
  );

  const requireModel = (
    provider: Provider,
    model: string,
    purpose: string,
  ) => {
    const current = useAppStore.getState();
    if (!model.trim()) {
      throw new Error(
        `Nessun modello selezionato per ${purpose}. Apri la relativa configurazione e scegli un modello esplicito.`,
      );
    }
    if (!current.modelCatalog[provider].some((item) => item.id === model)) {
      throw new Error(
        `Il modello "${model}" configurato per ${purpose} non è disponibile nella lista corrente. Aggiorna i modelli e scegli una nuova voce.`,
      );
    }
    if (provider !== "universal") {
      const status = current.providerStatuses[provider];
      if (!status?.hasApiKey) {
        throw new Error(
          `Chiave ${provider === "openrouter" ? "OpenRouter" : "Google AI Studio"} assente. Configurala in Impostazioni → Provider AI.`,
        );
      }
      if (status.valid === false) {
        throw new Error(
          `La chiave ${provider === "openrouter" ? "OpenRouter" : "Google AI Studio"} non è valida.`,
        );
      }
    }
  };

  const validateEngineConfiguration = () => {
    const settings = useAppStore.getState().settings;
    if (settings.agentTeam.enabled) {
      requireModel(
        settings.agentTeam.orchestrator.provider,
        settings.agentTeam.orchestrator.model,
        "l'orchestratore dell'Agent Team",
      );
      for (const agent of settings.agentTeam.agents) {
        requireModel(agent.provider, agent.model, `l'agente ${agent.name}`);
      }
      if (settings.agentTeam.verification.enabled) {
        requireModel(
          settings.agentTeam.verification.provider,
          settings.agentTeam.verification.model,
          "il verifier dell'Agent Team",
        );
      }
      requireModel(
        settings.agentTeam.synthesizer.provider,
        settings.agentTeam.synthesizer.model,
        "il sintetizzatore dell'Agent Team",
      );
    } else if (settings.council.enabled) {
      settings.council.models.forEach((participant, index) =>
        requireModel(
          participant.provider,
          participant.model,
          `il partecipante ${index + 1} del Council`,
        ),
      );
      requireModel(
        settings.council.synthesis.provider,
        settings.council.synthesis.model,
        "il sintetizzatore del Council",
      );
    } else {
      requireModel(settings.provider, settings.model, "la chat standard");
    }
  };

  const activeAssistant = () =>
    store.assistants.find((item) => item.id === conversation?.assistantId);

  /** Artifacts are only advertised to the model when the panel can receive them. */
  const artifactsEnabled = () =>
    store.settings.autoArtifacts &&
    (activeAssistant()?.capabilities.artifacts ?? true);

  const effectivePrompt = (message?: ChatMessage) => {
    if (!conversation) return store.settings.systemPrompt;
    return compileSystemPrompt(
      buildPromptLayers({
        system: store.settings.systemPrompt,
        conversation,
        project: store.projects.find((item) => item.id === conversation.projectId),
        assistant: activeAssistant(),
        message,
        ...(artifactsEnabled() ? { artifactPrompt: ARTIFACT_PROMPT } : {}),
      }),
    );
  };

  /**
   * The active mode nudges sampling, and an assistant may pin a temperature.
   * Both sit above the user's sliders; an explicit assistant setting wins.
   */
  const effectiveParams = (): ModelParameters => {
    const base = store.settings.params;
    const mode = findMode(conversation?.mode);
    const assistant = activeAssistant();
    return {
      ...base,
      ...mode.params,
      ...(assistant?.temperature !== undefined
        ? { temperature: assistant.temperature }
        : {}),
    };
  };

  const webSearchEnabled = () =>
    store.settings.webSearch ||
    store.settings.deepResearch ||
    (activeAssistant()?.capabilities.webSearch ?? false);

  /**
   * Lifts qualifying blocks out of a finished reply into artifacts and rewrites
   * the message body to reference them. Runs once, at completion, so the panel
   * never receives half-streamed content.
   */
  const finalizeAssistantMessage = (
    conversationId: string,
    messageId: string,
    result: { content: string; usage: Usage; sources: SourceCitation[]; raw: RawTrace },
    extra: Partial<ChatMessage> = {},
  ) => {
    const extraction = artifactsEnabled()
      ? extractArtifacts({ id: messageId, content: result.content })
      : { artifacts: [], content: result.content };
    const artifacts: Artifact[] = extraction.artifacts.map((artifact) => ({
      ...artifact,
      conversationId,
      revisions: [],
    }));
    if (artifacts.length) {
      store.addArtifacts(artifacts);
      // Reveal the newest artifact the way Claude does — but not on a narrow
      // viewport, where the panel is full-width and would bury the reply the
      // user just asked for.
      if (window.innerWidth > 1050) {
        store.setSettings({ rightPanel: "artifact" });
      }
    }
    store.updateMessage(conversationId, messageId, {
      ...result,
      ...extra,
      content: extraction.content,
      ...(artifacts.length
        ? { artifactIds: artifacts.map((artifact) => artifact.id) }
        : {}),
      status: "complete",
    });
  };

  const rawOverride = (): Record<string, unknown> | undefined => {
    if (!store.settings.rawMode || !store.settings.rawPayloadOverride.trim()) return undefined;
    const parsed: unknown = JSON.parse(store.settings.rawPayloadOverride);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("Il payload raw personalizzato deve essere un oggetto JSON.");
    }
    return parsed as Record<string, unknown>;
  };

  const executeRequest = async (
    request: ChatRequest,
    onDelta: (text: string) => void,
    signal: AbortSignal,
  ): Promise<{
    content: string;
    usage: Usage;
    sources: SourceCitation[];
    raw: RawTrace;
  }> => {
    let content = "";
    let usage: Usage | undefined;
    let firstTokenAt: number | undefined;
    const startedAt = performance.now();
    const sources: SourceCitation[] = [];
    const raw: RawTrace = {
      requestPayload: request.rawPayloadOverride ?? request,
      effectiveSystemPrompt: request.systemPrompt,
      includedMessages: request.messages,
      responseChunks: [],
    };
    await streamChat(request, signal, (event: StreamEvent) => {
      if (event.type === "delta") {
        if (!firstTokenAt) firstTokenAt = performance.now();
        content += event.text;
        onDelta(event.text);
      } else if (event.type === "usage") {
        usage = {
          ...event.usage,
          latencyMs: Math.round(performance.now() - startedAt),
          ...(firstTokenAt
            ? { timeToFirstTokenMs: Math.round(firstTokenAt - startedAt) }
            : {}),
        };
      } else if (event.type === "sources") {
        sources.push(...event.sources);
      } else if (event.type === "raw") {
        raw.responseChunks.push(event.data);
      } else if (event.type === "meta") {
        raw.providerMetadata = event.data;
      } else if (event.type === "error") {
        raw.error = event.error;
        throw new Error(JSON.stringify(event.error));
      }
    });
    usage ??= {
      inputTokens: 0,
      outputTokens: 0,
      totalTokens: 0,
      latencyMs: Math.round(performance.now() - startedAt),
      ...(firstTokenAt ? { timeToFirstTokenMs: Math.round(firstTokenAt - startedAt) } : {}),
      model: request.model,
      provider: request.provider,
    };
    return { content, usage, sources, raw };
  };

  const sendStandard = async (userMessage: ChatMessage) => {
    if (!conversation) return;
    const assistantId = crypto.randomUUID();
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
      createdAt: Date.now(),
      parentId: userMessage.id,
      branchId: userMessage.branchId,
      model: store.settings.model,
      provider: store.settings.provider,
      status: "streaming",
    };
    store.addMessage(conversation.id, assistantMessage);
    const current = useAppStore.getState().conversations.find((item) => item.id === conversation.id)!;
    const branchMessages = await hydrateMessageAttachments(
      visibleBranch(current.messages, userMessage.branchId),
    );
    const request: ChatRequest = {
      provider: store.settings.provider,
      model: store.settings.model,
      systemPrompt: effectivePrompt(userMessage),
      messages: branchMessages
        .filter((message) => message.id !== assistantId)
        .map((message) => ({
          role: message.role,
          content: message.content,
          ...(message.attachments ? { attachments: message.attachments } : {}),
        })),
      parameters: effectiveParams(),
      webSearch: webSearchEnabled(),
      deepResearch: store.settings.deepResearch,
      rawPayloadOverride: rawOverride(),
    };
    try {
      const result = await executeRequest(
        request,
        (delta) => {
          const latest = useAppStore
            .getState()
            .conversations.find((item) => item.id === conversation.id)
            ?.messages.find((item) => item.id === assistantId);
          store.updateMessage(conversation.id, assistantId, {
            content: `${latest?.content ?? ""}${delta}`,
          });
        },
        abortRef.current!.signal,
      );
      finalizeAssistantMessage(conversation.id, assistantId, result);
    } catch (caught) {
      const stopped = abortRef.current?.signal.aborted;
      store.updateMessage(conversation.id, assistantId, {
        status: stopped ? "stopped" : "error",
        content:
          useAppStore
            .getState()
            .conversations.find((item) => item.id === conversation.id)
            ?.messages.find((item) => item.id === assistantId)?.content ||
          (stopped ? "Generazione interrotta." : "Errore durante la generazione."),
      });
      if (!stopped) throw caught;
    }
  };

  const sendCouncil = async (userMessage: ChatMessage) => {
    if (!conversation) return;
    const startedAt = Date.now();
    const config = store.settings.council;
    const schedule = buildCouncilSchedule(config.models, config.rounds);
    const contributions: CouncilContribution[] = schedule.flatMap((wave) =>
      wave.map((participant) => ({
        id: crypto.randomUUID(),
        provider: participant.provider,
        model: participant.model,
        round: participant.round,
        phase: participant.phase,
        content: "",
        status: "queued",
      })),
    );
    setCouncil(contributions);
    let totalKnownCost = 0;
    for (const scheduledWave of schedule) {
      const gate = canStartModelCall(
        abortRef.current!.signal,
        totalKnownCost,
        config.maxCost,
      );
      if (!gate.allowed) {
        for (const contribution of contributions) {
          if (contribution.status === "queued") contribution.status = gate.reason!;
        }
        setCouncil([...contributions]);
        break;
      }
      const round = scheduledWave[0]?.round ?? 1;
      const previousRounds = contributions
        .filter((item) => item.round < round && item.status === "complete")
        .map((item) => ({
          provider: item.provider,
          model: item.model,
          round: item.round,
          content: item.content,
        }));
      const wave = contributions.filter((item) => item.round === round);
      for (const contribution of wave) contribution.status = "running";
      setCouncil([...contributions]);
      // Participants in a wave run concurrently and must all see exactly the
      // same prior-round record — a per-participant running transcript would
      // make the round order-dependent and non-reproducible.
      await Promise.all(
        wave.map(async (contribution) => {
          const request: ChatRequest = {
            provider: contribution.provider,
            model: contribution.model,
            systemPrompt: `${effectivePrompt(userMessage)}\n\nAct as a rigorous AI Council participant.`,
            messages: [
              {
                role: "user",
                content: buildCouncilParticipantPrompt(
                  userMessage.content,
                  contribution.round,
                  previousRounds,
                ),
                ...(userMessage.attachments
                  ? { attachments: userMessage.attachments }
                  : {}),
              },
            ],
            parameters: effectiveParams(),
            webSearch: webSearchEnabled(),
            deepResearch: store.settings.deepResearch,
            rawPayloadOverride: rawOverride(),
          };
          try {
            const result = await executeRequest(
              request,
              (delta) => {
                contribution.content += delta;
                setCouncil([...contributions]);
              },
              abortRef.current!.signal,
            );
            contribution.content = result.content;
            contribution.usage = result.usage;
            contribution.status = "complete";
            totalKnownCost += result.usage?.cost ?? 0;
          } catch {
            contribution.status = abortRef.current!.signal.aborted
              ? "aborted"
              : "error";
          }
          setCouncil([...contributions]);
        }),
      );
    }

    if (abortRef.current!.signal.aborted) {
      for (const contribution of contributions) {
        if (contribution.status === "queued" || contribution.status === "running") {
          contribution.status = "aborted";
        }
      }
      setCouncil([...contributions]);
      return;
    }
    if (
      !canStartModelCall(
        abortRef.current!.signal,
        totalKnownCost,
        config.maxCost,
      ).allowed
    ) {
      store.addMessage(conversation.id, {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `AI Council interrotto prima della sintesi: limite di costo noto raggiunto ($${totalKnownCost.toFixed(5)} / $${config.maxCost.toFixed(2)}).`,
        createdAt: Date.now(),
        parentId: userMessage.id,
        branchId: userMessage.branchId,
        model: config.synthesis.model,
        provider: config.synthesis.provider,
        status: "stopped",
      });
      return;
    }

    const completed = contributions
      .filter((item) => item.status === "complete")
      .map((item) => ({
        provider: item.provider,
        model: item.model,
        round: item.round,
        content: item.content,
      }));
    const assistantId = crypto.randomUUID();
    store.addMessage(conversation.id, {
      id: assistantId,
      role: "assistant",
      content: "",
      createdAt: Date.now(),
      parentId: userMessage.id,
      branchId: userMessage.branchId,
      model: config.synthesis.model,
      provider: config.synthesis.provider,
      status: "streaming",
    });
    const synthesisRequest: ChatRequest = {
      provider: config.synthesis.provider,
      model: config.synthesis.model,
      systemPrompt: `${effectivePrompt(userMessage)}

You are the final adjudicator of an AI Council. Evaluate material divergences, factual and logical correctness, quality of evidence, unsupported claims, and unresolved uncertainty. Preserve valuable minority insights and do not reduce the result to a summary of consensus.`,
      messages: [
        {
          role: "user",
          content: buildCouncilSynthesisPrompt(userMessage.content, completed),
          ...(userMessage.attachments ? { attachments: userMessage.attachments } : {}),
        },
      ],
      parameters: effectiveParams(),
      webSearch: false,
      deepResearch: false,
      rawPayloadOverride: rawOverride(),
    };
    try {
      const result = await executeRequest(
        synthesisRequest,
        (delta) => {
          const latest = useAppStore
            .getState()
            .conversations.find((item) => item.id === conversation.id)
            ?.messages.find((item) => item.id === assistantId);
          store.updateMessage(conversation.id, assistantId, {
            content: `${latest?.content ?? ""}${delta}`,
          });
        },
        abortRef.current!.signal,
      );
      finalizeAssistantMessage(
        conversation.id,
        assistantId,
        {
          ...result,
          usage: {
            ...result.usage,
            cost: totalKnownCost + (result.usage.cost ?? 0),
          },
        },
        // The live timeline is transient state; freezing it onto the message is
        // what lets the debate be reopened after a reload.
        { run: councilRunRecord(contributions, startedAt, totalKnownCost) },
      );
      setCouncil([]);
    } catch (caught) {
      const stopped = abortRef.current!.signal.aborted;
      store.updateMessage(conversation.id, assistantId, {
        status: stopped ? "stopped" : "error",
        content: stopped
          ? "Sintesi del Council interrotta."
          : "Errore durante la sintesi del Council.",
      });
      if (!stopped) throw caught;
    }
  };

  const sendAgentTeam = async (userMessage: ChatMessage) => {
    if (!conversation) return;
    const startedAt = Date.now();
    const config = store.settings.agentTeam;
    const orchestratorRun: AgentRun = {
      id: crypto.randomUUID(),
      agentId: "__orchestrator",
      name: "Orchestrator",
      role: "Pianificazione e delega",
      provider: config.orchestrator.provider,
      model: config.orchestrator.model,
      task: "Analizza la richiesta e distribuisci il lavoro.",
      dependsOn: [],
      content: "",
      status: "planning",
    };
    const runs: AgentRun[] = [
      orchestratorRun,
      ...config.agents.map((agent) => ({
        id: crypto.randomUUID(),
        agentId: agent.id,
        name: agent.name,
        role: agent.role,
        provider: agent.provider,
        model: agent.model,
        task: agent.instructions,
        dependsOn: agent.dependsOn,
        content: "",
        status: agent.dependsOn.length ? "waiting" as const : "queued" as const,
      })),
    ];
    setAgentRuns([...runs]);
    const graph = resolveAgentWaves(config.agents);
    if (graph.error) {
      for (const run of runs.slice(1)) {
        run.status = "blocked";
        run.detail = graph.error;
      }
      orchestratorRun.status = "error";
      orchestratorRun.detail = `Validazione runtime fallita: ${graph.error}`;
      setAgentRuns([...runs]);
      store.addMessage(conversation.id, {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `Agent Team non avviato: ${graph.error}`,
        createdAt: Date.now(),
        parentId: userMessage.id,
        branchId: userMessage.branchId,
        model: config.synthesizer.model,
        provider: config.synthesizer.provider,
        status: "error",
      });
      return;
    }
    const roster = config.agents.map((agent) => ({
      id: agent.id,
      name: agent.name,
      role: agent.role,
      instructions: agent.instructions,
      dependsOn: agent.dependsOn,
    }));
    const planningRequest: ChatRequest = {
      provider: config.orchestrator.provider,
      model: config.orchestrator.model,
      systemPrompt: `${effectivePrompt(userMessage)}\n\nYou are an agent-team orchestrator. Return a precise execution plan.`,
      messages: [{
        role: "user",
        content: `Original request:\n${userMessage.content}\n\nAvailable agents:\n${JSON.stringify(roster, null, 2)}\n\nReturn ONLY valid JSON with shape {"tasks":[{"agentId":"...","objective":"..."}]}. Assign a concrete objective to every agent. Respect configured dependencies and make handoffs explicit in each objective.`,
        ...(userMessage.attachments ? { attachments: userMessage.attachments } : {}),
      }],
      parameters: {
        ...effectiveParams(),
        temperature: Math.min(effectiveParams().temperature, 0.3),
      },
      webSearch: false,
      deepResearch: false,
      rawPayloadOverride: rawOverride(),
    };
    let totalKnownCost = 0;
    try {
      const planResult = await executeRequest(
        planningRequest,
        (delta) => {
          orchestratorRun.content += delta;
          setAgentRuns([...runs]);
        },
        abortRef.current!.signal,
      );
      orchestratorRun.content = planResult.content;
      orchestratorRun.usage = planResult.usage;
      orchestratorRun.status = "complete";
      totalKnownCost += planResult.usage.cost ?? 0;
      const parsedPlan = parseAgentPlan(planResult.content);
      const fallbackPlan = buildFallbackPlan(config.agents);
      const missingAssignments = config.agents.filter(
        (agent) => !parsedPlan.has(agent.id),
      );
      if (missingAssignments.length) {
        orchestratorRun.status = "fallback";
        orchestratorRun.detail = `Piano incompleto: fallback applicato a ${missingAssignments.map((agent) => agent.name).join(", ")}.`;
      }
      for (const run of runs.slice(1)) {
        run.task =
          parsedPlan.get(run.agentId) ?? fallbackPlan.get(run.agentId) ?? run.task;
      }
    } catch {
      if (abortRef.current!.signal.aborted) {
        orchestratorRun.status = "aborted";
        orchestratorRun.detail = "Pianificazione interrotta dall'utente.";
        setAgentRuns([...runs]);
        return;
      }
      const fallbackPlan = buildFallbackPlan(config.agents);
      orchestratorRun.status = "fallback";
      orchestratorRun.detail =
        "Il piano dell'orchestratore non è disponibile: usato il piano deterministico di fallback.";
      for (const run of runs.slice(1)) {
        run.task = fallbackPlan.get(run.agentId) ?? run.task;
      }
    }
    setAgentRuns([...runs]);

    const pending = new Set(runs.slice(1).map((run) => run.agentId));
    while (pending.size && !abortRef.current!.signal.aborted && totalKnownCost < config.maxCost) {
      const ready = runs.slice(1).filter(
        (run) =>
          pending.has(run.agentId) &&
          run.dependsOn.every((dependency) =>
            runs.some((candidate) => candidate.agentId === dependency && candidate.status === "complete"),
          ),
      );
      if (!ready.length) {
        const blockedIds = [...pending];
        const detail = `Deadlock runtime: dipendenze non completate per ${blockedIds.join(", ")}.`;
        for (const run of runs.slice(1)) {
          if (pending.has(run.agentId)) {
            run.status = "blocked";
            run.detail = detail;
          }
        }
        break;
      }
      for (const run of ready) {
        run.status = "running";
        pending.delete(run.agentId);
      }
      setAgentRuns([...runs]);
      await Promise.all(
        ready.map(async (run) => {
          const agent = config.agents.find((item) => item.id === run.agentId)!;
          const isolatedPrompt = buildAgentPrompt({
            originalRequest: userMessage.content,
            objective: run.task,
            dependencyOutputs: run.dependsOn.flatMap((dependency) => {
              const source = runs.find((item) => item.agentId === dependency);
              return source
                ? [{ name: source.name, content: source.content }]
                : [];
            }),
          });
          const request: ChatRequest = {
            provider: run.provider,
            model: run.model,
            systemPrompt: `${effectivePrompt(userMessage)}\n\nYou are ${agent.name}, a specialized ${agent.role} agent.\n${agent.instructions}`,
            messages: [{
              role: "user",
              content: isolatedPrompt,
              ...(userMessage.attachments ? { attachments: userMessage.attachments } : {}),
            }],
            parameters: effectiveParams(),
            webSearch:
              webSearchEnabled() || /research|fact/i.test(`${run.role} ${run.name}`),
            deepResearch:
              store.settings.deepResearch && /research/i.test(`${run.role} ${run.name}`),
            rawPayloadOverride: rawOverride(),
          };
          try {
            const result = await executeRequest(
              request,
              (delta) => {
                run.content += delta;
                setAgentRuns([...runs]);
              },
              abortRef.current!.signal,
            );
            run.content = result.content;
            run.usage = result.usage;
            run.status = "complete";
            totalKnownCost += result.usage.cost ?? 0;
          } catch {
            run.status = abortRef.current!.signal.aborted ? "aborted" : "error";
            run.detail = abortRef.current!.signal.aborted
              ? "Esecuzione interrotta dall'utente."
              : "La chiamata del sub-agente non è riuscita.";
          }
          setAgentRuns([...runs]);
        }),
      );
    }
    for (const run of runs.slice(1)) {
      if (
        pending.has(run.agentId) &&
        (run.status === "waiting" || run.status === "queued")
      ) {
        const gate = canStartModelCall(
          abortRef.current!.signal,
          totalKnownCost,
          config.maxCost,
        );
        run.status = gate.reason === "aborted" ? "aborted" : "cost_limit";
        run.detail =
          gate.reason === "aborted"
            ? "Esecuzione interrotta dall'utente."
            : `Limite di costo noto raggiunto: $${totalKnownCost.toFixed(5)} / $${config.maxCost.toFixed(2)}.`;
      }
    }
    setAgentRuns([...runs]);

    if (abortRef.current!.signal.aborted) return;
    const teamDossier = runs
      .slice(1)
      .map(
        (run) =>
          `## ${run.name} — ${run.role} [${run.status}]\nObjective: ${run.task}\n\n${run.content}`,
      )
      .join("\n\n");
    let verificationOutput = "";
    if (config.verification.enabled) {
      const verifierRun: AgentRun = {
        id: crypto.randomUUID(),
        agentId: "__verifier",
        name: "Verifier",
        role: "Verifica e critica",
        provider: config.verification.provider,
        model: config.verification.model,
        task: config.verification.instructions,
        dependsOn: config.agents.map((agent) => agent.id),
        content: "",
        status: "queued",
      };
      runs.push(verifierRun);
      const verificationGate = canStartModelCall(
        abortRef.current!.signal,
        totalKnownCost,
        config.maxCost,
      );
      if (!verificationGate.allowed) {
        verifierRun.status =
          verificationGate.reason === "aborted" ? "aborted" : "cost_limit";
        verifierRun.detail =
          verificationGate.reason === "aborted"
            ? "Esecuzione interrotta dall'utente."
            : "Verifica non avviata: limite di costo noto raggiunto.";
      } else {
        verifierRun.status = "running";
        setAgentRuns([...runs]);
        const verificationRequest: ChatRequest = {
          provider: config.verification.provider,
          model: config.verification.model,
          systemPrompt: `${effectivePrompt(userMessage)}\n\nYou are an independent verifier for an agent team.`,
          messages: [
            {
              role: "user",
              content: buildVerificationPrompt(
                userMessage.content,
                teamDossier,
                config.verification.instructions,
              ),
              ...(userMessage.attachments
                ? { attachments: userMessage.attachments }
                : {}),
            },
          ],
          parameters: {
            ...effectiveParams(),
            temperature: Math.min(effectiveParams().temperature, 0.3),
          },
          webSearch: store.settings.webSearch,
          deepResearch: false,
          rawPayloadOverride: rawOverride(),
        };
        try {
          const result = await executeRequest(
            verificationRequest,
            (delta) => {
              verifierRun.content += delta;
              setAgentRuns([...runs]);
            },
            abortRef.current!.signal,
          );
          verifierRun.content = result.content;
          verifierRun.usage = result.usage;
          verifierRun.status = "complete";
          verificationOutput = result.content;
          totalKnownCost += result.usage.cost ?? 0;
        } catch {
          verifierRun.status = abortRef.current!.signal.aborted
            ? "aborted"
            : "error";
          verifierRun.detail = abortRef.current!.signal.aborted
            ? "Esecuzione interrotta dall'utente."
            : "La verifica non è riuscita; la sintesi userà il dossier disponibile.";
        }
      }
      setAgentRuns([...runs]);
    }

    if (abortRef.current!.signal.aborted) return;
    if (
      !canStartModelCall(
        abortRef.current!.signal,
        totalKnownCost,
        config.maxCost,
      ).allowed
    ) {
      store.addMessage(conversation.id, {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `Agent Team interrotto prima della sintesi: limite di costo noto raggiunto ($${totalKnownCost.toFixed(5)} / $${config.maxCost.toFixed(2)}).`,
        createdAt: Date.now(),
        parentId: userMessage.id,
        branchId: userMessage.branchId,
        model: config.synthesizer.model,
        provider: config.synthesizer.provider,
        status: "stopped",
      });
      return;
    }
    const assistantId = crypto.randomUUID();
    store.addMessage(conversation.id, {
      id: assistantId,
      role: "assistant",
      content: "",
      createdAt: Date.now(),
      parentId: userMessage.id,
      branchId: userMessage.branchId,
      model: config.synthesizer.model,
      provider: config.synthesizer.provider,
      status: "streaming",
    });
    const dossier = runs.slice(1).filter((run) => run.agentId !== "__verifier")
      .map((run) => `## ${run.name} — ${run.role} [${run.status}]\nObjective: ${run.task}\n\n${run.content}`)
      .join("\n\n");
    const synthesisRequest: ChatRequest = {
      provider: config.synthesizer.provider,
      model: config.synthesizer.model,
      systemPrompt: `${effectivePrompt(userMessage)}\n\nYou are the lead synthesizer of a specialized agent team.`,
      messages: [{
        role: "user",
        content: buildTeamSynthesisPrompt(
          userMessage.content,
          dossier,
          verificationOutput,
        ),
        ...(userMessage.attachments ? { attachments: userMessage.attachments } : {}),
      }],
      parameters: effectiveParams(),
      webSearch: false,
      deepResearch: false,
      rawPayloadOverride: rawOverride(),
    };
    try {
      const result = await executeRequest(
        synthesisRequest,
        (delta) => {
          const latest = useAppStore.getState().conversations
            .find((item) => item.id === conversation.id)?.messages
            .find((item) => item.id === assistantId);
          store.updateMessage(conversation.id, assistantId, { content: `${latest?.content ?? ""}${delta}` });
        },
        abortRef.current!.signal,
      );
      finalizeAssistantMessage(
        conversation.id,
        assistantId,
        {
          ...result,
          usage: {
            ...result.usage,
            cost: totalKnownCost + (result.usage.cost ?? 0),
          },
        },
        { run: teamRunRecord(runs, startedAt, totalKnownCost) },
      );
      setAgentRuns([]);
    } catch (caught) {
      const stopped = abortRef.current!.signal.aborted;
      store.updateMessage(conversation.id, assistantId, {
        status: stopped ? "stopped" : "error",
        content: stopped
          ? "Sintesi dell'Agent Team interrotta."
          : "Errore durante la sintesi dell'Agent Team.",
      });
      if (!stopped) throw caught;
    }
  };

  const runConfiguredEngine = async (userMessage: ChatMessage) => {
    const settings = useAppStore.getState().settings;
    const engine = engineAfterQuestions(settings);
    if (engine === "agent_team") await sendAgentTeam(userMessage);
    else if (engine === "council") await sendCouncil(userMessage);
    else await sendStandard(userMessage);
  };

  const finishQuestionCollection = async (
    session: QuestionSession,
    terminal: "sufficient" | "limit_reached",
    attachments?: Attachment[],
  ) => {
    if (!conversation) return;
    const generating: QuestionSession = {
      ...session,
      status: "generating_final",
      updatedAt: Date.now(),
    };
    store.upsertQuestionSession(generating);
    const dossierMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: buildQuestionDossier(generating),
      createdAt: Date.now(),
      branchId: generating.branchId,
      hidden: true,
      ...(attachments?.length ? { attachments } : {}),
    };
    store.addMessage(conversation.id, dossierMessage);
    await runConfiguredEngine(dossierMessage);
    store.upsertQuestionSession({
      ...generating,
      status: terminal,
      updatedAt: Date.now(),
    });
  };

  const analyzeQuestions = async (
    session: QuestionSession,
    attachments?: Attachment[],
  ) => {
    if (!conversation) return;
    const currentConversation = useAppStore
      .getState()
      .conversations.find((item) => item.id === conversation.id);
    if (!currentConversation) return;
    const config = currentConversation.questionMode;
    const provider = config.useChatModel
      ? useAppStore.getState().settings.provider
      : config.provider;
    const model = config.useChatModel
      ? useAppStore.getState().settings.model
      : config.model;
    requireModel(provider, model, "la Modalità domande");

    const analyzing: QuestionSession = {
      ...session,
      status: "analyzing",
      round: session.round + 1,
      updatedAt: Date.now(),
    };
    store.upsertQuestionSession(analyzing);
    const assistantId = crypto.randomUUID();
    store.addMessage(conversation.id, {
      id: assistantId,
      role: "assistant",
      content: "Analisi della richiesta e delle informazioni mancanti…",
      createdAt: Date.now(),
      branchId: analyzing.branchId,
      provider,
      model,
      status: "streaming",
    });

    const request: ChatRequest = {
      provider,
      model,
      systemPrompt: `${effectivePrompt()}\n\nYou run a clarification-question phase. Return only the requested JSON and never answer the original request yet.`,
      messages: [
        {
          role: "user",
          content: buildQuestionAnalysisPrompt(
            analyzing,
            Math.max(
              1,
              config.maxQuestions - analyzing.questions.length,
            ),
          ),
          ...(attachments?.length ? { attachments } : {}),
        },
      ],
      parameters: {
        ...store.settings.params,
        temperature: Math.min(store.settings.params.temperature, 0.3),
      },
      webSearch: false,
      deepResearch: false,
      rawPayloadOverride: rawOverride(),
    };

    try {
      const result = await executeRequest(
        request,
        () => {
          // The model emits machine-readable JSON; it is parsed before display.
        },
        abortRef.current!.signal,
      );
      const decision = parseQuestionDecision(result.content);
      const newQuestions = capNewQuestions(
        analyzing.questions.length,
        config.maxQuestions,
        selectNewQuestions(analyzing, decision),
      );
      const noProgressCount = newQuestions.length
        ? 0
        : analyzing.noProgressCount + 1;
      const next: QuestionSession = {
        ...analyzing,
        questions: [...analyzing.questions, ...newQuestions],
        noProgressCount,
        updatedAt: Date.now(),
        status: "collecting",
      };
      const loopState = shouldEndQuestionLoop(
        next,
        config.maxRounds,
        config.maxQuestions,
      );
      if (
        decision.status === "sufficient" ||
        loopState !== "continue" ||
        !newQuestions.length
      ) {
        const terminal =
          decision.status === "sufficient" ? "sufficient" : "limit_reached";
        store.updateMessage(conversation.id, assistantId, {
          content:
            terminal === "sufficient"
              ? "Informazioni sufficienti. Avvio la risposta finale."
              : "Raccolta conclusa per limite o mancato progresso. Procedo con le informazioni disponibili.",
          status: "complete",
          usage: result.usage,
          raw: result.raw,
        });
        store.upsertQuestionSession({ ...next, status: terminal });
        await finishQuestionCollection(next, terminal, attachments);
        return;
      }
      const awaiting: QuestionSession = {
        ...next,
        status: "awaiting_user",
      };
      store.upsertQuestionSession(awaiting);
      store.updateMessage(conversation.id, assistantId, {
        content: formatQuestions(newQuestions),
        status: "complete",
        usage: result.usage,
        raw: result.raw,
      });
    } catch (caught) {
      const stopped = abortRef.current!.signal.aborted;
      store.updateMessage(conversation.id, assistantId, {
        content: stopped
          ? "Modalità domande annullata."
          : `Errore durante l'analisi dei chiarimenti: ${
              caught instanceof Error ? caught.message : String(caught)
            }`,
        status: stopped ? "stopped" : "error",
      });
      store.upsertQuestionSession({
        ...analyzing,
        status: "cancelled",
        updatedAt: Date.now(),
      });
      if (!stopped) throw caught;
    }
  };

  const proceedWithAvailableInformation = async () => {
    if (!questionSession || !conversation || isStreaming) return;
    validateEngineConfiguration();
    abortRef.current = new AbortController();
    setIsStreaming(true);
    try {
      await finishQuestionCollection(questionSession, "sufficient");
    } catch (caught) {
      if (!abortRef.current.signal.aborted) {
        setError(caught instanceof Error ? caught.message : String(caught));
      }
    } finally {
      setIsStreaming(false);
    }
  };

  const skipQuestions = async () => {
    if (!questionSession || isStreaming) return;
    const skipped: QuestionSession = {
      ...questionSession,
      questions: questionSession.questions.map((question) =>
        question.answer || question.skipped
          ? question
          : { ...question, skipped: true },
      ),
      noProgressCount: questionSession.noProgressCount + 1,
      updatedAt: Date.now(),
    };
    abortRef.current = new AbortController();
    setIsStreaming(true);
    try {
      await analyzeQuestions(skipped);
    } finally {
      setIsStreaming(false);
    }
  };

  const cancelQuestionSession = () => {
    if (!questionSession || !conversation) return;
    abortRef.current?.abort();
    store.upsertQuestionSession({
      ...questionSession,
      status: "cancelled",
      updatedAt: Date.now(),
    });
    store.addMessage(conversation.id, {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "Raccolta dei chiarimenti annullata.",
      createdAt: Date.now(),
      branchId: questionSession.branchId,
      status: "stopped",
    });
  };

  const send = async ({ content, attachments, instruction, replaceAfterMessageId }: SendOptions) => {
    if (!conversation || (!content.trim() && !attachments?.length) || isStreaming) return;
    setError(undefined);
    try {
      rawOverride();
      validateEngineConfiguration();
      if (conversation.questionMode.enabled) {
        const provider = conversation.questionMode.useChatModel
          ? store.settings.provider
          : conversation.questionMode.provider;
        const model = conversation.questionMode.useChatModel
          ? store.settings.model
          : conversation.questionMode.model;
        requireModel(provider, model, "la Modalità domande");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      return;
    }
    if (replaceAfterMessageId) store.truncateAfter(conversation.id, replaceAfterMessageId);
    const branchId = replaceAfterMessageId ? crypto.randomUUID() : conversation.activeBranchId;
    if (replaceAfterMessageId) store.updateConversation(conversation.id, { activeBranchId: branchId });
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: content.trim(),
      createdAt: Date.now(),
      branchId,
      ...(attachments?.length ? { attachments } : {}),
      ...(instruction?.trim() ? { instruction: instruction.trim() } : {}),
    };
    store.addMessage(conversation.id, userMessage);
    if (conversation.messages.length === 0) {
      store.updateConversation(conversation.id, {
        title: content.trim().slice(0, 54) || attachments?.[0]?.name || "Nuova conversazione",
      });
    }
    abortRef.current = new AbortController();
    setIsStreaming(true);
    try {
      if (
        questionSession &&
        questionSession.status === "awaiting_user"
      ) {
        const answer = content.trim();
        const answered: QuestionSession = {
          ...questionSession,
          questions: questionSession.questions.map((question) =>
            question.answer || question.skipped
              ? question
              : { ...question, answer },
          ),
          noProgressCount: answer
            ? 0
            : questionSession.noProgressCount + 1,
          updatedAt: Date.now(),
        };
        store.upsertQuestionSession(answered);
        await analyzeQuestions(answered, attachments);
      } else if (conversation.questionMode.enabled) {
        const session: QuestionSession = {
          id: crypto.randomUUID(),
          conversationId: conversation.id,
          branchId,
          originalRequest: userMessage.content,
          originalMessageId: userMessage.id,
          questions: [],
          round: 0,
          noProgressCount: 0,
          status: "analyzing",
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        store.upsertQuestionSession(session);
        await analyzeQuestions(session, attachments);
      } else {
        await runConfiguredEngine(userMessage);
      }
    } catch (caught) {
      if (!abortRef.current.signal.aborted) {
        setError(caught instanceof Error ? caught.message : String(caught));
      }
    } finally {
      setIsStreaming(false);
    }
  };

  const stop = () => abortRef.current?.abort();

  /**
   * Shared tail for the paths that re-run an existing turn. These used to lack
   * both a configuration check and a catch, so a missing model surfaced as an
   * unhandled rejection with nothing shown in the UI.
   */
  const rerun = async (user: ChatMessage) => {
    if (!conversation || isStreaming) return;
    setError(undefined);
    try {
      rawOverride();
      validateEngineConfiguration();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      return;
    }
    store.truncateAfter(conversation.id, user.id);
    abortRef.current = new AbortController();
    setIsStreaming(true);
    try {
      await runConfiguredEngine(user);
    } catch (caught) {
      if (!abortRef.current.signal.aborted) {
        setError(caught instanceof Error ? caught.message : String(caught));
      }
    } finally {
      setIsStreaming(false);
    }
  };

  const regenerate = async (message: ChatMessage) => {
    if (!conversation) return;
    const messages = visibleBranch(conversation.messages, message.branchId);
    const index = messages.findIndex((item) => item.id === message.id);
    const user = [...messages.slice(0, index)]
      .reverse()
      .find((item) => item.role === "user");
    if (!user) return;
    await rerun(user);
  };

  const editMessage = async (message: ChatMessage, content: string) => {
    if (!conversation || isStreaming) return;
    store.updateMessage(conversation.id, message.id, {
      content,
      updatedAt: Date.now(),
      status: message.status ?? "complete",
    });
    if (message.role === "assistant") {
      store.truncateAfter(conversation.id, message.id);
      return;
    }
    await rerun({ ...message, content, updatedAt: Date.now() });
  };

  return {
    conversation,
    isStreaming,
    council,
    agentRuns,
    questionSession,
    error,
    send,
    stop,
    regenerate,
    editMessage,
    proceedWithAvailableInformation,
    skipQuestions,
    cancelQuestionSession,
    effectivePrompt,
  };
}
